import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * The page's "now" for what the app decides by time: the default time on a
 * dose form, elapsed time on pending authorizations. It is the server's logic
 * clock (server/clock.ts): real time in live; in the test environment, real
 * time plus the Test clock. The session poll keeps the offset current.
 */
let offsetMs = 0;

/** From the session response: the server's logic clock minus its real clock. */
export function syncClock(serverNow: number, clockNow: number | undefined): void {
  offsetMs = typeof clockNow === "number" && Number.isFinite(clockNow) ? clockNow - serverNow : 0;
}

export function appNow(): number {
  return Date.now() + offsetMs;
}

export interface TestClockState {
  set: boolean;
  offsetMs: number;
  nowUtc: string;
  label: string;
  window: string;
}

/** Fired after the Test clock is set or cleared, so the banner re-reads it at once. */
export const TEST_CLOCK_EVENT = "medadmin:testclock";

/** The Test clock as the banner shows it (test environment only; null elsewhere or when unknown). */
export function useTestClock(enabled: boolean): [TestClockState | null, () => void] {
  const [state, setState] = useState<TestClockState | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) {
      setState(null);
      return;
    }
    let live = true;
    const load = () =>
      void api<{ clock: TestClockState }>("GET", "/api/test/clock?peek=1").then((r) => {
        if (!live) return;
        if (r.ok) {
          setState(r.data.clock);
          offsetMs = r.data.clock.offsetMs;
        } else setState(null);
      });
    load();
    const t = window.setInterval(load, 60_000);
    const on = () => load();
    window.addEventListener(TEST_CLOCK_EVENT, on);
    return () => {
      live = false;
      window.clearInterval(t);
      window.removeEventListener(TEST_CLOCK_EVENT, on);
    };
  }, [enabled, tick]);
  return [state, () => setTick((n) => n + 1)];
}

/** Back to real time (test environment). */
export async function clearTestClock(): Promise<void> {
  await api("DELETE", "/api/test/clock");
  window.dispatchEvent(new Event(TEST_CLOCK_EVENT));
}
